import { z } from 'zod';
import { AlertEventSchema } from './alertEvent';
import { ApplicationSchema } from './application';
import { AlertSeveritySchema, IsoTimestampSchema, UuidSchema } from './common';
import { LogWithRelationsSchema } from './log';

export const MetricsRangeQuerySchema = z.object({
  from: IsoTimestampSchema,
  to: IsoTimestampSchema,
});

const TimelineBucketSchema = z.object({
  bucket: IsoTimestampSchema,
  counts: z.record(z.number().int().nonnegative()),
});

const AppHealthRowSchema = z.object({
  application: ApplicationSchema.pick({
    id: true,
    name: true,
    product: true,
    company: true,
    instance: true,
    environment: true,
  }),
  success: z.number().int().nonnegative(),
  warning: z.number().int().nonnegative(),
  error: z.number().int().nonnegative(),
  neutral: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  healthRate: z.number().min(0).max(1),
});

const DistributionEntrySchema = z.object({
  messageTypeId: UuidSchema,
  count: z.number().int().nonnegative(),
});

// An alert event carrying what the timeline needs to describe it on hover without a
// round trip per marker: the rule, the application and the log that fired it.
export const TimelineAlertSchema = AlertEventSchema.extend({
  // True when the (rule, application) pair was still in cooldown, so nothing was sent.
  silenced: z.boolean(),
  ruleName: z.string(),
  severity: AlertSeveritySchema,
  applicationName: z.string(),
  // null once retention has purged the triggering log; the event itself outlives it.
  log: z
    .object({
      timestamp: IsoTimestampSchema,
      message: z.string(),
      messageTypeName: z.string().nullable(),
      messageTypeColor: z.string().nullable(),
    })
    .nullable(),
});

export const OverviewMetricsSchema = z.object({
  totals: z.object({
    applications: z.number().int().nonnegative(),
    logs: z.number().int().nonnegative(),
    errorRate: z.number().min(0).max(1),
    previousErrorRate: z.number().min(0).max(1).nullable(),
    // Time-based liveness over the range: fraction of the observed window in which
    // the applications in scope were emitting. null = nothing observable (no active
    // app in scope), which the UI must render as "sin datos", not as 100%.
    uptime: z.number().min(0).max(1).nullable(),
    // Seconds of silence counted as downtime inside that same window.
    downSeconds: z.number().nonnegative(),
    alertsTriggered: z.number().int().nonnegative(),
  }),
  lifetime: z.object({
    totalLogs: z.number().int().nonnegative(),
    errorLogs: z.number().int().nonnegative(),
  }),
  timeline: z.array(TimelineBucketSchema),
  appsHealth: z.array(AppHealthRowSchema),
  distribution: z.array(DistributionEntrySchema),
  recentCriticalEvents: z.array(LogWithRelationsSchema),
  recentAlerts: z.array(TimelineAlertSchema),
});

export const ApplicationMetricsSchema = OverviewMetricsSchema.extend({
  avgLatency: z.number().nullable(),
  p95Latency: z.number().nullable(),
});

export type MetricsRangeQuery = z.infer<typeof MetricsRangeQuerySchema>;
export type OverviewMetrics = z.infer<typeof OverviewMetricsSchema>;
export type ApplicationMetrics = z.infer<typeof ApplicationMetricsSchema>;
export type AppHealthRow = z.infer<typeof AppHealthRowSchema>;
export type TimelineAlert = z.infer<typeof TimelineAlertSchema>;
